<template>
  <div v-if="activeFilters.length > 0" class="d-flex align-center flex-wrap ga-2 mb-4">
    <span class="text-caption text-medium-emphasis">{{ t('activeFilters') }}</span>

    <v-chip v-for="filter in activeFilters"
            :key="filter.visualId"
            size="small"
            label
            closable
            color="primary"
            variant="tonal"
            @click:close="remove(filter.visualId)">
      {{ filter.text }}
    </v-chip>

    <v-btn size="small"
           variant="text"
           density="comfortable"
           class="text-none"
           @click="clearAll">
      {{ t('clearAll') }}
    </v-btn>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const store = useStore();
const { t } = useI18n();

// The store can swap the context object on reload, and its `filters` map is
// markRaw - so snapshot on every filtersLastUpdated bump rather than binding
// to the map directly.
const filterContext = computed(() => store.state.filterContext);
const activeFilters = ref([]);

// Fallback label per visual when the chart did not supply one.
const VISUAL_LABELS = {
  assetStatusBar: 'status',
  assetSystemStackedBarChart: 'assetSystem',
  assetTypeStackedBarChart: 'assetType',
  assetCategoryChart: 'category',
  drawingLocationChart: 'drawing',
  ratingChart: 'rating',
  completenessChart: 'completeness',
  inspectionsDueChart: 'nextInspection',
  inspectionGantt: 'inspectionSchedule',
  installationCostChart: 'installationCost',
  outcomeChart: 'outcome',
  inspectionIntervalBoxChart: 'inspectionInterval',
  plannedInspected_drawing: 'drawing',
  plannedInspected_category: 'category',
  plannedInspected_type: 'assetType',
  overdueKpi: 'lateOverdue',
  due30Kpi: 'due30Days',
  due60Kpi: 'due31to60Days',
};

const describe = (filter) => {
  const fallbackKey = VISUAL_LABELS[filter.visualId];
  const name = filter.label || (fallbackKey ? t(fallbackKey) : filter.visualId);
  return `${name} (${filter.count})`;
};

const refresh = () => {
  const context = filterContext.value;
  if (!context || typeof context.getActiveFilters !== 'function') {
    activeFilters.value = [];
    return;
  }
  activeFilters.value = context.getActiveFilters().map((filter) => ({
    ...filter,
    text: describe(filter),
  }));
};

const remove = (visualId) => filterContext.value?.removeFilter(visualId);
const clearAll = () => filterContext.value?.resetFilters();

watch(
  () => [filterContext.value?.filtersLastUpdated?.value, store.getters.getDataLoaded],
  refresh,
  { immediate: true }
);
</script>
